import {Button, Typography} from '@material-ui/core';
import CardActions from '@material-ui/core/CardActions';
import {Async, AsyncError, matchAsync} from 'Core/models/Async';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import Modal from 'Shared/components/Modal';
import unionize, {ofType, UnionOf} from 'unionize';
import {fetchAgents, offboardAgent} from 'Users/ducks/agentAdministration';
import {getFullName} from 'Users/models/Agent';
import {UserModel} from 'Users/models/UserModel';
import './OffboardingConfirmation.sass';

type OffboardingConfirmationProps = {
  agent: UserModel;
  onClose: () => any;
  offboardAgent: typeof offboardAgent;
  fetchAgents: typeof fetchAgents;
};

export const OffboardingAgentState = unionize({
  ActiveAgent: ofType<UserModel>(),
  Error: ofType<AsyncError>(),
  Loading: ofType(),
  OffboardedAgent: ofType<UserModel>(),
});

type OffboardingAgentStateUnion = UnionOf<typeof OffboardingAgentState>;

export const toOffboardingState = (asyncAgent: Async<UserModel>) => {
  return matchAsync(asyncAgent, {
    Loaded: agent => (false ? OffboardingAgentState.OffboardedAgent(agent) : OffboardingAgentState.ActiveAgent(agent)),
    Loading: () => OffboardingAgentState.Loading(),
    ServerError: error => OffboardingAgentState.Error(error),
    Unauthorized: error => OffboardingAgentState.Error(error),
    Conflict: error => OffboardingAgentState.Error(error),
  });
};

export const OffboardingConfirmation = (props: OffboardingConfirmationProps) => {
  const [agentToOffboard, setAgentToOffboard] = useState<OffboardingAgentStateUnion>(
    OffboardingAgentState.ActiveAgent(props.agent)
  );

  const {agent, onClose, fetchAgents} = props;
  const {firstName, lastName} = agent;

  useEffect(() => {
    if (OffboardingAgentState.is.OffboardedAgent(agentToOffboard)) {
      onClose();
      fetchAgents();
    }
  }, [agentToOffboard, onClose, fetchAgents]);

  const AgentContent = (
    <>
      <Typography component="div" className="offboarding-confirmation__confirmation-description">
        Are you sure you want to offboard agent {getFullName({firstName: firstName!, lastName: lastName!})}?
      </Typography>
      <p className="offboarding-confirmation__compliance-message">
        Offboarding an agent will remove access to agency policies. However, the agent will still be allowed to access
        commission statements for 6 months from the offboarding date.
      </p>
      <CardActions className="offboarding-confirmation__actions">
        <Button
          id="offboard-agent-button"
          color="primary"
          variant="contained"
          onClick={() => {
            props.offboardAgent(1, compose(setAgentToOffboard, toOffboardingState));
          }}
        >
          Offboard
        </Button>
        <Button variant="contained" onClick={props.onClose}>
          Cancel
        </Button>
      </CardActions>
    </>
  );

  return (
    <Modal
      onClose={props.onClose}
      open
      title={
        <>
          <div className="offboarding-confirmation__title">
            <div className="offboarding-confirmation__title-message">Confirm Offboarding</div>
          </div>
        </>
      }
    >
      <div className="offboarding-confirmation__modal-body">
        {OffboardingAgentState.match(agentToOffboard, {
          ActiveAgent: () => AgentContent,
          Error: error => (
            <>
              <ErrorPage error={error} />
              <CardActions>
                <Button onClick={props.onClose} fullWidth variant="contained" color="primary">
                  Close
                </Button>
              </CardActions>
            </>
          ),
          Loading: () => <Loading />,
          OffboardedAgent: () => <Loading />,
        })}
      </div>
    </Modal>
  );
};

export default connect(undefined, {fetchAgents, offboardAgent})(OffboardingConfirmation);
